import { AdServerGamSettings } from '@mbrtargeting/metatag-config-types';
import { SystemEvent } from '@mbrtargeting/metatag-shared-types/metatag-core';
import { isArray, isNotUndefined, partition } from '@mbrtargeting/metatag-utils';
import { GLOBAL_TARGETING } from '../../../core/token.js';
import { inject, injectionTarget } from '../../../loader/decorators/inject.js';
import { onPhase, Phase } from '../../../loader/decorators/on-phase.js';
import { onSystemEvent } from '../../../loader/decorators/on-system-event.js';
import { ConfigResolver } from '../../../loader/essentials/config-resolver.js';
import { selectAdserverGamSettings, selectAdserverRingierSettings, selectPositionSettings } from '../../../loader/essentials/config-selector.js';
import { logger } from '../../../loader/essentials/logger.js';
import { Map2 } from '../../../loader/map2.js';
import { ConsentCache } from '../../../loader/modules/consent-cache.js';
import { CONFIG_RESOLVER, CONSENT_CACHE } from '../../../loader/token.js';
import { MeasureMetaTagPerformance, measurePerformance } from '../../../utils/performance.js';
import { setIdsForAdSlot } from '../../utils/adslot-data-store.js';
import { constructAdUnitPath } from '../../utils/construct-adunit-path.js';
import { AdserverLoadAction, AdserverLoadState, AdslotAdserverLoadState } from '../adslot-controller/adslot-adserver-load-state.js';
import { LifecycleAction } from '../adslot-controller/adslot-lifecycle-state.js';
import { Adslot } from '../adslot-controller/adslot.js';
import { CreativeSource, CreativeSourceLoadSlotsParams } from '../adslot-controller/creative-source.js';
import { GlobalTargeting } from '../global-targeting/global-targeting.js';
import { gptDefineOutOfPageSlot, gptDefineSlot, gptDestroySlots, gptLoadScript, gptReady, sendToGpt } from './gpt-helper.js';

const log = logger.logGroup({ prefix: 'Adserver' });

interface GamAdserverAdslotData {
    state: AdslotAdserverLoadState,
    gptContainer: HTMLDivElement,
    gptSlot?: googletag.Slot,
    firstLoad: boolean;
    registerLifecycleListener: boolean;
    gptSlotNeedsDestroy: boolean;
    measure: MeasureMetaTagPerformance;
}

abstract class AbstractGamAdserver implements CreativeSource {

    @inject(CONFIG_RESOLVER) protected configResolver!: ConfigResolver;
    @inject(CONSENT_CACHE) protected consentCache!: ConsentCache;
    @inject(GLOBAL_TARGETING) protected globalTargeting!: GlobalTargeting;
    protected adserverConfig: AdServerGamSettings

    protected adServerName: string;

    private data = new Map2<Adslot, GamAdserverAdslotData>((adslot) => ({
        firstLoad: true,
        registerLifecycleListener: true,
        gptSlotNeedsDestroy: false,
        state: new AdslotAdserverLoadState(adslot, this.adServerName),
        gptSlot: undefined,
        gptContainer: adslot.getStableDivElement({
            id: `sdgGptContainer-${this.adserverConfig.config.networkId}-${adslot.slotName}`,
        }),
        measure: measurePerformance(`Auction-${adslot.slotName.toUpperCase()}-GAM`).start()
    }));

    constructor(
        type: string,
    ) {
        this.adserverConfig = this.configResolver.get(type === 'ringier' ? selectAdserverRingierSettings() : selectAdserverGamSettings());
        this.adServerName = `GAM (${this.adserverConfig?.config?.networkId})`;
    }

    @onSystemEvent(SystemEvent.SDG_CORE_FILE_LOADED)
    public async init(): Promise<void> {
        if (!this.adserverConfig?.active) return;

        const { delayScriptCallUntilConsent = true, iabVendorId = 755, loadGptScript = true } = this.adserverConfig?.config || {};

        if (!loadGptScript) {
            log.debug('%s: GPT Script loading is disabled', [this.adServerName]);
            return;
        }

        if (!delayScriptCallUntilConsent) {
            return gptLoadScript();
        }

        const allowed: boolean = await this.consentCache.whenConsentConditions([
            { consentedVendors: [iabVendorId] },
            { legitimateInterestVendors: [iabVendorId] },
            { gdprApplies: false },
        ]);

        if (allowed) {
            return gptLoadScript();
        }
    }

    @onPhase({ phase: Phase.DELIVERY })
    public async configure(): Promise<void> {
        if (!this.adserverConfig?.active) return;

        const { gpt, pubads } = await gptReady();
        const { safeframeSandbox = false, setCentering = false, useDisableInitialLoad = false, useSingleRequest = false, collapseEmptyDivs = false } = this.adserverConfig?.config || {};

        if (safeframeSandbox) {
            log.debug('%s: Setting DFP SafeFrame to Sandbox mode', [this.adServerName]);
            pubads.setSafeFrameConfig({ sandbox: true });
        }
        if (setCentering) {
            log.debug('%s: Setting DFP to center Ads', [this.adServerName]);
            pubads.setCentering(true);
        }
        if (useDisableInitialLoad) {
            log.info('%s: Setting disableInitialLoad', [this.adServerName]);
            pubads.disableInitialLoad();
        }
        if (useSingleRequest) {
            log.info('%s: Setting DFP to SingleRequest Render Mode', [this.adServerName]);
            pubads.enableSingleRequest();
        }
        if (collapseEmptyDivs) {
            log.debug('%s: Setting DFP to collapse DIVs', [this.adServerName]);
            pubads.collapseEmptyDivs(true);
        }
        if (gpt.pubadsReady) {
            log.info('%s: already enabled. AdServer ready.', [this.adServerName]);
        } else {
            log.info('%s: setup finished. GPT Services enabled. AdServer ready.', [this.adServerName]);
            gpt.enableServices();
        }

        const getAdSlotByGptSlot = (gptslot: googletag.Slot): Adslot | undefined => {
            for (const [adslot, data] of this.data.entries()) {
                if (data.gptSlot && data.gptSlot === gptslot) return adslot;
            }
        };

        // pubads.addEventListener('slotOnload', (event: googletag.events.SlotOnloadEvent) => {
        // });
        pubads.addEventListener('slotRequested', (event: googletag.events.SlotRequestedEvent) => {
            // get adslot for given event
            const adslot: Adslot | undefined = getAdSlotByGptSlot(event.slot);
            // adslot is undefined if we receive an event of a gpt parallel setup
            // mark the adslot as requested
            adslot && this.data.get(adslot).state.dispatch(AdserverLoadAction.REQUEST);
        });
        pubads.addEventListener('slotResponseReceived', (event: googletag.events.SlotResponseReceived) => {
            // get adslot for given event
            const adslot: Adslot | undefined = getAdSlotByGptSlot(event.slot);
            // adslot is undefined if we receive an event of an gpt parallel setup
            // mark the adslot as responded
            adslot && this.data.get(adslot).state.dispatch(AdserverLoadAction.RESPOND);
        });

        /**
         * Google iframe source is loaded but creative inside of frame is not fully rendered. Only event containing google ad manager id's to be sent to metatag.
         */
        pubads.addEventListener('slotRenderEnded', (event: googletag.events.SlotRenderEndedEvent) => {
            // get adslot for given event
            const adslot: Adslot | undefined = getAdSlotByGptSlot(event.slot);
            // adslot is undefined if we receive an event of an gpt parallel setup
            if (adslot) {
                const { size } = event;
                // extract and save the adserver ids from the event
                setIdsForAdSlot(adslot.slotName, {
                    source: 'gpt',
                    values: {
                        advertiserId: String(event.advertiserId ?? 0),
                        orderId: String(event.campaignId ?? 0),
                        creativeId: String(event.creativeId ?? event.sourceAgnosticCreativeId ?? 0),
                        lineItemId: String(event.lineItemId ?? event.sourceAgnosticLineItemId ?? 0),
                        isEmpty: event.isEmpty,
                        size: isArray(size) ? size.join('x') : size,
                        adUnitPath: event.slot.getAdUnitPath(),
                        redirectCreativeId: String(event.sourceAgnosticCreativeId ?? 0),
                        redirectLineItemId: String(event.sourceAgnosticLineItemId ?? 0),
                        deliveryType: 'gam',
                    },
                });
                if (!event.isEmpty && isArray(size)) {
                    const [width, height] = size;
                    adslot.reportAdDelivery(width, height, false);
                }

                // mark the adslot as NOAD or RENDER
                this.data.get(adslot).state.dispatch(event.isEmpty ? AdserverLoadAction.NOAD : AdserverLoadAction.FILL);
            }
        });
    }

    @onSystemEvent(SystemEvent.SDG_ZONE_SET, { once: false }) // for single-page-applications
    onZoneChange() {
        this.data.forEach(data => data.gptSlotNeedsDestroy = true);
    }

    public async loadSlots({ adslots, done }: CreativeSourceLoadSlotsParams): Promise<void> {
        if (!this.adserverConfig?.active) {
            log.debug('%s: is not active, pass slots to next creativeSource %o', [this.adServerName, adslots.map(slot => slot.slotName)]);
            done(adslots);
            return;
        }

        // find data per adslot
        const adslotsAndData = adslots.map<[Adslot, GamAdserverAdslotData]>(adslot => [adslot, this.data.get(adslot)]);
        log.debug('%s: calling slots: %o', [this.adServerName, adslots.map(slot => slot.slotName.toUpperCase())]);

        // TODO how to group adslots again?
        for (const [adslot, data] of adslotsAndData) {
            data.state.onState(AdserverLoadState.EMPTY, async () => done([adslot]), { once: true });
            data.state.onState(AdserverLoadState.FILLED, async () => done([adslot]), { once: true });
            data.state.onState(AdserverLoadState.RESPONDED, async () => {
                data.measure.end();
            }, { once: true });
        }

        // add global targeting
        await sendToGpt((_, pubads) => {
            // update global targeting
            const globalTargetingObject = this.globalTargeting.toObject();
            Object.entries(globalTargetingObject).forEach(([key, values]) => {
                pubads.setTargeting(key, values);
            });
            log.debug('%s: KeyValue GlobalTargeting update: %o', [this.adServerName, globalTargetingObject]);
        });

        // define the slots
        for (const [adslot, data] of adslotsAndData) {
            if (data.gptSlotNeedsDestroy && data.gptSlot) {
                await gptDestroySlots([data.gptSlot]); // TODO destroy all flagged slots at once
                data.firstLoad = true;
                data.gptSlotNeedsDestroy = false;
                data.gptSlot = undefined;
            }
            data.gptSlot ||= await this.createGptSlot(adslot, data.gptContainer);

            if (data.registerLifecycleListener) {
                data.registerLifecycleListener = false;
                //when slot is unregistered & still present, remove GPT slot
                adslot.lifecycle.onAction(LifecycleAction.DEREGISTER, async () => {
                    if (data.gptSlot) {
                        // do not await, as this would block the lifecycle and delete of local reference
                        gptDestroySlots([data.gptSlot]);
                        data.gptSlot = undefined;
                    }
                });
            }
            // gptSlot and data prepared, add targeting to slot
            await sendToGpt(() => {
                const gptSlot = data.gptSlot;
                const localTargetingObject = adslot.getTargeting().toObject();
                gptSlot!.clearTargeting();
                gptSlot!.updateTargetingFromMap(localTargetingObject);
                //todo remove later, when problems are solved. Unnecessarily bloats log
                log.debug('%s: %s: KeyValue LocalTargeting update: %o', [this.adServerName, adslot.slotName.toUpperCase(), localTargetingObject]);
            });
        }
        await sendToGpt((gpt, pubads) => {
            // ready gptSlot to be loadable
            for (const [/*adslot*/, { firstLoad, gptSlot }] of adslotsAndData) {
                if (firstLoad && gptSlot) {
                    gptSlot.addService(pubads);
                }
            }
        });

        // loading and refresh of adslots
        await sendToGpt((gpt, pubads) => {
            // reattach gptContainer to adserverContainer if someone removed it
            for (const [adslot, { gptContainer }] of adslotsAndData) {
                if (gptContainer && !gptContainer.parentElement) {
                    adslot.adserverContainer.appendChild(gptContainer);
                }
            }
            // display
            for (const [/*adslot*/, { firstLoad, gptSlot }] of adslotsAndData) {
                if (firstLoad && gptSlot) {
                    gpt.display(gptSlot);
                }
            }
            // refresh
            const requiresRefresh = pubads.isInitialLoadDisabled();
            const refreshSlots = adslotsAndData.filter(([/*adslot*/, { firstLoad }]) => !firstLoad || requiresRefresh).map(([/*adslot*/, { gptSlot }]) => gptSlot!);
            if (refreshSlots.length) {
                pubads.clear(refreshSlots);
                pubads.refresh(refreshSlots);
            }
            // update internal data
            for (const [/*adslot*/, data] of adslotsAndData) {
                data.firstLoad = false;
            }
        });
    }

    protected async createGptSlot(adslot: Adslot, gptContainer: HTMLDivElement): Promise<googletag.Slot> {
        const logPrefix = `${this.adServerName}: ${adslot.slotName.toUpperCase()}:`;
        const { dfpTagType = 'standardGpt' } = this.configResolver.get(selectPositionSettings(adslot.slotName)) || {};
        const adUnitPath = constructAdUnitPath(adslot);

        switch (dfpTagType) {
            case 'outOfPageGpt':
                return gptDefineOutOfPageSlot({
                    logPrefix,
                    adUnitPath,
                    divId: gptContainer.id,
                });
            case 'interstitialGpt':
                return gptDefineOutOfPageSlot({
                    logPrefix,
                    adUnitPath,
                    outOfPageFormat: googletag.enums.OutOfPageFormat.INTERSTITIAL,
                });
            case 'standardGpt':
            default:
                return gptDefineSlot({
                    logPrefix,
                    adUnitPath,
                    sizes: adslot.getSizes(),
                    divId: gptContainer.id,
                });
        }
    }
}

@injectionTarget()
export class StroeerGamAdserver extends AbstractGamAdserver {

    constructor() {
        super('dfp');
    }
}


const postfixIfValue = (postfix: string, value: string | undefined): string => value ? `${value}${postfix}` : '';

@injectionTarget()
export class RingierGamAdserver extends AbstractGamAdserver {

    constructor() {
        super('ringier');
    }

    public async loadSlots({ adslots, done, ...moreParams }: CreativeSourceLoadSlotsParams): Promise<void> {
        if (!this.adserverConfig?.active) {
            log.debug('%s: is not active, pass slots to next creativeSource %o', [this.adServerName, adslots.map(slot => slot.slotName.toUpperCase())]);
            done(adslots);
            return;
        }

        const [ringierAdslots, nonringierAdslots] = partition(adslots, adslot => !!this.configResolver.get(selectPositionSettings(adslot.slotName))?.ringier);

        if (nonringierAdslots.length) {
            log.debug('%s: skipping slots to next creativeSource %o', [this.adServerName, nonringierAdslots.map(slot => slot.slotName.toUpperCase())]);
            done(nonringierAdslots);
        }

        if (ringierAdslots.length) {
            super.loadSlots({
                adslots: ringierAdslots,
                done,
                ...moreParams,
            });
        }
    }

    protected override async createGptSlot(adslot: Adslot, gptContainer: HTMLDivElement): Promise<googletag.Slot> {
        const logPrefix = `${this.adServerName}: ${adslot.slotName.toUpperCase()}:`;
        const { networkId, mcmId, mobileAdUnit, desktopAdUnit } = this.adserverConfig?.config ?? {};
        const { isMobileSlot = false, dfpTagType = 'standardGpt', ringier } = this.configResolver.get(selectPositionSettings(adslot.slotName)) || {};
        const isHome = /start|home/.test(adslot.getZone());
        const { name, dfpSizes, kvPreset = {} } = ringier?.[isHome ? 'index' : 'details'] ?? {};

        adslot.setTargeting(kvPreset); //route ringier kvPreset into adslot targeting

        const constructRingierAdUnitPath = (parts: Array<string | undefined>): Array<string | undefined> => {
            const fullPath: string[] = [];
            parts.reduce((previousValue, currentValue) => {
                const result = postfixIfValue('_', previousValue) + currentValue;
                fullPath.push(result);
                return result;
            }, '');
            return [
                mcmId?.length ? `/${networkId},${mcmId}` : `/${networkId}`,
                ...fullPath
            ];
        };

        const adUnitPath = constructRingierAdUnitPath([
            isMobileSlot ? mobileAdUnit : desktopAdUnit,
            isMobileSlot ? 'MobileWeb' : 'Desktop',
            isHome ? 'Home' : 'ROS',
            name,
        ]).filter(isNotUndefined).join('/');


        switch (dfpTagType) {
            case 'outOfPageGpt':
                return gptDefineOutOfPageSlot({
                    logPrefix,
                    adUnitPath,
                    divId: gptContainer.id,
                });
            case 'interstitialGpt':
                return gptDefineOutOfPageSlot({
                    logPrefix,
                    adUnitPath,
                    outOfPageFormat: googletag.enums.OutOfPageFormat.INTERSTITIAL,
                });
            case 'standardGpt':
            default:
                return gptDefineSlot({
                    logPrefix,
                    adUnitPath,
                    sizes: dfpSizes ?? [],
                    divId: gptContainer.id,
                });
        }
    }
}
